<template>
  <div class="tcla" dir="rtl">
    <!-- Header -->
    <div class="tcla-header">
      <div class="tcla-header__top">
        <div class="tcla-header__info">
          <h1 class="tcla-header__title">
            <span class="tcla-header__icon">🔗</span>
            تحلیل خوشه‌های لینک‌دهی داخلی
          </h1>
          <p class="tcla-header__subtitle">نمایش صفحات بر اساس سلسله‌مراتب کلمات کلیدی و روابط لینک‌دهی</p>
        </div>
        <div class="tcla-header__badges">
          <span class="tcla-badge tcla-badge--blue">{{ totalClusters }} خوشه</span>
          <span class="tcla-badge tcla-badge--green">{{ totalLinkedPages }} صفحه</span>
          <span class="tcla-badge" :class="overallBadgeClass">{{ overallStatusText }}</span>
          <span class="tcla-badge" @click="copy_data()" :class="overallBadgeClass"><CIcon name="cil-clone" /></span>
        </div>
      </div>

      <!-- Stats -->
      <div class="tcla-stats">
        <div class="tcla-stat tcla-stat--success">
          <div class="tcla-stat__value">{{ correctLinks }}</div>
          <div class="tcla-stat__label">لینک‌های صحیح</div>
          <div class="tcla-stat__bar">
            <div class="tcla-stat__bar-fill" :style="{ width: correctPercent + '%' }"></div>
          </div>
        </div>
        <div class="tcla-stat tcla-stat--danger">
          <div class="tcla-stat__value">{{ incorrectLinks }}</div>
          <div class="tcla-stat__label">لینک‌های اشتباه</div>
          <div class="tcla-stat__bar">
            <div class="tcla-stat__bar-fill" :style="{ width: incorrectPercent + '%' }"></div>
          </div>
        </div>
        <div class="tcla-stat tcla-stat--warning">
          <div class="tcla-stat__value">{{ missingLinks }}</div>
          <div class="tcla-stat__label">لینک‌های از دست رفته</div>
          <div class="tcla-stat__bar">
            <div class="tcla-stat__bar-fill" :style="{ width: missingPercent + '%' }"></div>
          </div>
        </div>
        <div class="tcla-stat tcla-stat--neutral">
          <div class="tcla-stat__value">{{ totalInternalLinks }}</div>
          <div class="tcla-stat__label">کل لینک‌های داخلی</div>
          <div class="tcla-stat__bar">
            <div class="tcla-stat__bar-fill tcla-stat__bar-fill--full"></div>
          </div>
        </div>
      </div>
    </div>
{{items[0]}}
    <!-- Filters -->
    <div class="tcla-filters">
      <div class="tcla-filter">
        <label class="tcla-filter__label">مرتب‌سازی</label>
        <select class="tcla-filter__select" v-model="sortBy">
          <option v-for="opt in sortOptions" :key="opt.value" :value="opt.value">{{ opt.label }}</option>
        </select>
      </div>
      <div class="tcla-filter">
        <label class="tcla-filter__label">وضعیت</label>
        <select class="tcla-filter__select" v-model="filterStatus">
          <option v-for="opt in filterOptions" :key="opt.value" :value="opt.value">{{ opt.label }}</option>
        </select>
      </div>
      <div class="tcla-filter tcla-filter--search">
        <label class="tcla-filter__label">جستجو</label>
        <div class="tcla-search">
          <svg class="tcla-search__icon" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>
          <input class="tcla-search__input" type="text" placeholder="نام صفحه را وارد کنید..." v-model="searchQuery" />
        </div>
      </div>
    </div>

    <!-- Empty state -->
    <div v-if="filteredClusters.length === 0" class="tcla-empty">
      <div class="tcla-empty__icon">📭</div>
      <p class="tcla-empty__text">هیچ داده‌ای برای نمایش وجود ندارد</p>
      <p class="tcla-empty__hint" v-if="searchQuery || filterStatus !== 'all'">فیلترها را تغییر دهید یا جستجو را پاک کنید</p>
    </div>

    <!-- Clusters -->
    <div v-else class="tcla-clusters">
      <div
        v-for="(cluster, index) in filteredClusters"
        :key="index"
        class="tcla-cluster"
        :class="'tcla-cluster--' + cluster.health"
        :style="{ animationDelay: index * 0.06 + 's' }"
      >
        <!-- Cluster Header -->
        <div class="tcla-cluster__header" @click="toggleCluster(index)">
          <div class="tcla-cluster__header-right">
            <span class="tcla-cluster__folder">📁</span>
            <div>
              <h3 class="tcla-cluster__title">
                {{ cluster.title }}
                <span class="tcla-cluster__keyword-tag">({{ cluster.keyword }})</span>
                <CButton @click="open_edit_blog(cluster.source)" color="warning">
                  ویرایش
                </CButton>
              </h3>
              <div class="tcla-cluster__meta">
                <span class="tcla-cluster__health-dot" :class="'tcla-cluster__health-dot--' + cluster.health"></span>
                {{ getHealthLabel(cluster.health) }}
                <span class="tcla-cluster__meta-sep">·</span>
                {{ cluster.keywordCount }} کلمه کلیدی
                <span class="tcla-cluster__meta-sep">·</span>
                {{ cluster.linkedPages.length }} صفحه لینک‌دهنده
                <span class="tcla-cluster__meta-sep">·</span>
                {{ cluster.incomingLinks }} ورودی / {{ cluster.outgoingLinks }} خروجی
              </div>
            </div>
          </div>
          <svg
            class="tcla-cluster__chevron"
            :class="{ 'tcla-cluster__chevron--open': openClusters[index] }"
            width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"
          >
            <polyline points="6 9 12 15 18 9"/>
          </svg>
        </div>

        <!-- Cluster Body -->
        <div v-show="openClusters[index]" class="tcla-cluster__body">
          <!-- Description Rule -->
          <div class="tcla-rule">
            <span class="tcla-rule__icon">📐</span>
            <span class="tcla-rule__text">
              صفحات با کلمات کلیدی بیشتر ({{ cluster.keywordCount + 1 }}+ کلمه) باید به این صفحه ({{ cluster.keywordCount }} کلمه) لینک دهند
            </span>
            <span v-if="cluster.health === 'healthy'" class="tcla-rule__check">✓</span>
            <span v-else class="tcla-rule__warn">!</span>
          </div>

          <!-- Linked Pages -->
          <div v-if="cluster.linkedPages.length > 0" class="tcla-section">
            <h4 class="tcla-section__title">صفحات لینک‌دهنده به این خوشه</h4>
            <div class="tcla-linked-list">
              <div
                v-for="(page, pidx) in cluster.linkedPages"
                :key="pidx"
                class="tcla-linked"
                :class="{
                  'tcla-linked--ok': page.isCorrect && page.levelDifference === 1,
                  'tcla-linked--warn': page.isCorrect && page.levelDifference > 1,
                  'tcla-linked--err': !page.isCorrect
                }"
              >
                <div class="tcla-linked__left">
                  <span class="tcla-linked__status-icon">
                    <template v-if="page.isCorrect && page.levelDifference === 1">✓</template>
                    <template v-else-if="page.isCorrect && page.levelDifference > 1">⤳</template>
                    <template v-else>✕</template>
                  </span>
                  <div class="tcla-linked__info">
                    <span class="tcla-linked__title">{{ page.title }}
                    <CButton @click="open_edit_blog(page.source)" size="sm" color="secondary">ویرایش</CButton></span>
                    <span class="tcla-linked__anchor">لنگر: "{{ page.anchorText }}"</span>
                  </div>
                </div>
                <div class="tcla-linked__right">
                  <span class="tcla-linked__kw-count">{{ page.keywordCount }} کلمه</span>
                  <span class="tcla-linked__direction">{{ page.keywordCount }} → {{ cluster.keywordCount }}</span>
                  <span v-if="page.isCorrect && page.levelDifference === 1" class="tcla-tag tcla-tag--ok">صحیح</span>
                  <span v-else-if="page.isCorrect && page.levelDifference > 1" class="tcla-tag tcla-tag--warn">پرش {{ page.levelDifference }} سطحی</span>
                  <span v-else class="tcla-tag tcla-tag--err">اشتباه</span>
                  <button
                    v-if="!page.isCorrect || page.levelDifference > 1"
                    class="tcla-btn-link"
                    @click.stop="showSuggestion(page, cluster)"
                  >
                    راه حل
                  </button>
                </div>
              </div>
            </div>
          </div>

          <!-- Errors -->
          <div v-if="cluster.errors && cluster.errors.length > 0" class="tcla-section">
            <h4 class="tcla-section__title tcla-section__title--err">خطاها</h4>
            <div class="tcla-errors">
              <div v-for="(error, eidx) in cluster.errors" :key="eidx" class="tcla-error-item">
                {{ error }}
              </div>
            </div>
          </div>

          <!-- Missing Links -->
          <div v-if="cluster.missingLinks && cluster.missingLinks.length > 0" class="tcla-section">
            <h4 class="tcla-section__title tcla-section__title--warn">لینک‌های از دست رفته</h4>
            <div class="tcla-missing-list">
              <div
                v-for="(missing, midx) in cluster.missingLinks"
                :key="midx"
                class="tcla-missing"
              >
                <div class="tcla-missing__top">
                  <div class="tcla-missing__info">
                    <span class="tcla-missing__reason">{{ missing.reason }}</span>
                    <div class="tcla-missing__tags">
                      <span class="tcla-tag tcla-tag--blue">لنگر: "{{ missing.suggestedAnchor }}"</span>
                      <span class="tcla-tag tcla-tag--green">{{ missing.relationType }}</span>
                    </div>
                  </div>
                  <button class="tcla-btn tcla-btn--outline" @click="showMissingSuggestion(missing)">
                    مشاهده جزئیات
                  </button>
                  <button class="tcla-btn tcla-btn--outline" @click="open_edit_blog(missing.source)">
                    ویرایش مقاله
                  </button>
                </div>

                <!-- Common words -->
                <div v-if="missing.commonWords && missing.commonWords.length > 0" class="tcla-common-words">
                  <span class="tcla-common-words__label">کلمات مشترک:</span>
                  <span v-for="(word, widx) in missing.commonWords" :key="widx" class="tcla-word-chip">{{ word }}</span>
                </div>

                <!-- Suggestion Box -->
                <div class="tcla-suggestion-box">
                  <div class="tcla-suggestion-box__header">
                    <span>💡</span> پیشنهاد لینک‌سازی
                  </div>
                  <div class="tcla-suggestion-box__body">
                    <p>
                      از صفحه <strong>{{ missing.title }}</strong>
                      با لنگر <code>"{{ missing.suggestedAnchor }}"</code>
                      به صفحه <strong>{{ missing.targetTitle }}</strong> لینک دهید.
                    </p>
                    <p class="tcla-suggestion-box__example">
                      مثال: برای اطلاعات بیشتر در مورد <a>{{ missing.suggestedAnchor }}</a>، مقاله جامع ما را مطالعه کنید.
                    </p>
                    <button class="tcla-btn tcla-btn--primary tcla-btn--sm" @click="copyLinkSuggestion(missing)">
                      کپی متن لینک
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Modal: Suggestion -->
    <div v-if="showModal" class="tcla-modal-overlay" @click.self="showModal = false">
      <div class="tcla-modal">
        <div class="tcla-modal__header">
          <h3>💡 راه حل پیشنهادی</h3>
          <button class="tcla-modal__close" @click="showModal = false">✕</button>
        </div>
        <div class="tcla-modal__body" v-if="selectedSuggestion">
          <div class="tcla-modal__section">
            <h4>مشکل</h4>
            <p class="tcla-modal__problem">{{ selectedSuggestion.problem }}</p>
          </div>
          <div class="tcla-modal__section">
            <h4>راه حل</h4>
            <p class="tcla-modal__solution">{{ selectedSuggestion.solution }}</p>
          </div>
          <div class="tcla-modal__section">
            <h4>لنگر پیشنهادی</h4>
            <div class="tcla-modal__code-block">{{ selectedSuggestion.suggestedAnchor }}</div>
          </div>
          <div class="tcla-modal__section">
            <h4>مثال</h4>
            <div class="tcla-modal__example-block">{{ selectedSuggestion.example }}</div>
          </div>
        </div>
        <div class="tcla-modal__footer">
          <button class="tcla-btn tcla-btn--outline" @click="showModal = false">بستن</button>
        </div>
      </div>
    </div>

    <!-- Modal: Missing -->
    <div v-if="showMissingModal" class="tcla-modal-overlay" @click.self="showMissingModal = false">
      <div class="tcla-modal">
        <div class="tcla-modal__header">
          <h3>📝 جزئیات لینک از دست رفته</h3>
          <button class="tcla-modal__close" @click="showMissingModal = false">✕</button>
        </div>
        <div class="tcla-modal__body" v-if="selectedMissing">
          <div class="tcla-modal__section">
            <h4>صفحه مبدأ</h4>
            <p><strong>{{ selectedMissing.title }}</strong> ({{ selectedMissing.keywordCount }} کلمه کلیدی)</p>
            <p class="tcla-modal__muted">کلمه کلیدی: {{ selectedMissing.keyword }}</p>
          </div>
          <div class="tcla-modal__section">
            <h4>صفحه مقصد</h4>
            <p><strong>{{ selectedMissing.targetTitle }}</strong> ({{ selectedMissing.targetKeywordCount }} کلمه کلیدی)</p>
            <p class="tcla-modal__muted">کلمه کلیدی: {{ selectedMissing.targetKeyword }}</p>
          </div>
          <div class="tcla-modal__section">
            <h4>ارتباط موضوعی</h4>
            <p class="tcla-modal__solution">{{ selectedMissing.relationType }}</p>
          </div>
          <div v-if="selectedMissing.commonWords && selectedMissing.commonWords.length > 0" class="tcla-modal__section">
            <h4>کلمات مشترک</h4>
            <div>
              <span v-for="(word, widx) in selectedMissing.commonWords" :key="widx" class="tcla-word-chip">{{ word }}</span>
            </div>
          </div>
          <div class="tcla-modal__section">
            <h4>دلیل نیاز به لینک</h4>
            <p class="tcla-modal__warn">{{ selectedMissing.reason }}</p>
          </div>
          <div class="tcla-modal__section">
            <h4>لنگر پیشنهادی</h4>
            <div class="tcla-modal__code-block">{{ selectedMissing.suggestedAnchor }}</div>
          </div>
          <div class="tcla-modal__section">
            <h4>پیشنهاد لینک‌سازی</h4>
            <div class="tcla-modal__highlight">
              از صفحه <strong>{{ selectedMissing.title }}</strong> با لنگر
              <code>"{{ selectedMissing.suggestedAnchor }}"</code>
              به صفحه <strong>{{ selectedMissing.targetTitle }}</strong> لینک دهید.
            </div>
          </div>
          <div class="tcla-modal__section">
            <h4>کد HTML پیشنهادی</h4>
            <div class="tcla-modal__code-block tcla-modal__code-block--dark">
              &lt;a href="{{ selectedMissing.targetUrl || '#' }}"&gt;{{ selectedMissing.suggestedAnchor }}&lt;/a&gt;
            </div>
          </div>
          <div class="tcla-modal__section">
            <h4>متن پیشنهادی</h4>
            <div class="tcla-modal__highlight tcla-modal__highlight--blue">
              برای اطلاعات بیشتر در مورد {{ selectedMissing.suggestedAnchor }}، مقاله جامع ما را مطالعه کنید.
            </div>
          </div>
        </div>
        <div class="tcla-modal__footer">
          <button class="tcla-btn tcla-btn--outline" @click="showMissingModal = false">بستن</button>
          <button class="tcla-btn tcla-btn--primary" @click="copySuggestion">کپی متن</button>
        </div>
      </div>
    </div>

    <!-- Toast -->
    <transition name="toast">
      <div v-if="toastVisible" class="tcla-toast">
        <span class="tcla-toast__icon">✓</span>
        {{ toastMessage }}
      </div>
    </transition>
  </div>
</template>

<script>
export default {
  name: 'TopicClusterLinkAnalysis',
  props: {
    items: {
      type: Array,
      default: () => []
    },
    stopWords: {
      type: Array,
      default: () => [
        'از', 'با', 'برای', 'در', 'به', 'بر', 'روی', 'زیر', 'کنار',
        'جلوی', 'پشت', 'داخل', 'خارج', 'بالای', 'پایین', 'نزدیک',
        'دور', 'بین', 'میان', 'طریق', 'وسیلۀ', 'توسط', 'بجز', 'غیر',
        'مثل', 'مانند', 'همانند', 'چون', 'چرا', 'زیرا', 'چراکه',
        'که', 'تا', 'مگر', 'آیا', 'چه', 'چگونه', 'چطور', 'کجا',
        'کی', 'چند', 'چقدر', 'کدام', 'کدامین', 'این', 'آن', 'اینها',
        'آنها', 'ما', 'شما', 'او', 'ایشان', 'خود', 'خودی', 'یک', 'دو',
        'سه', 'چهار', 'پنج', 'شش', 'هفت', 'هشت', 'نه', 'ده', 'صد', 'هزار',
        'و', 'یا', 'اما', 'پس', 'پس‌از', 'پیش', 'پیش‌از', 'بعد', 'بعداز'
      ]
    }
  },
  data() {
    return {
      sortBy: 'keywordCount',
      filterStatus: 'all',
      searchQuery: '',
      showModal: false,
      showMissingModal: false,
      selectedSuggestion: null,
      selectedMissing: null,
      openClusters: {},
      toastVisible: false,
      toastMessage: '',
      sortOptions: [
        { value: 'keywordCount', label: 'تعداد کلمات کلیدی (صعودی)' },
        { value: 'keywordCountDesc', label: 'تعداد کلمات کلیدی (نزولی)' },
        { value: 'health', label: 'وضعیت سلامت' },
        { value: 'links', label: 'تعداد لینک‌ها' }
      ],
      filterOptions: [
        { value: 'all', label: 'همه خوشه‌ها' },
        { value: 'healthy', label: 'سالم' },
        { value: 'warning', label: 'هشدار' },
        { value: 'unhealthy', label: 'مشکل‌دار' }
      ]
    };
  },
  computed: {
    clusters() {
      if (!this.items || this.items.length === 0) return [];

      const clusters = [];
      const pageMap = this.buildPageMap();
      const groupedPages = this.groupPagesByKeywordCount(pageMap);

      Object.keys(groupedPages).forEach(keywordCount => {
        const pages = groupedPages[keywordCount];
console.log("pages is",pages)
        pages.forEach(page => {
          const cluster = {
            title: page.title,
            keyword: page.keyword,
            source: page.source,
            keywordCount: page.keywordCount,
            health: 'healthy',
            linkedPages: [],
            errors: [],
            missingLinks: [],
            incomingLinks: 0,
            outgoingLinks: page.outgoingLinks.length,
            url: page.url
          };

          const incomingPages = this.getIncomingPages(page, pageMap);

          incomingPages.forEach(incoming => {
            const isCorrect = incoming.keywordCount > page.keywordCount;
            const linkInfo = this.findLinkInfo(incoming, page);
            const levelDiff = incoming.keywordCount - page.keywordCount;

            cluster.linkedPages.push({
              title: incoming.title,
              keyword: incoming.keyword,
              source: incoming.source,
              keywordCount: incoming.keywordCount,
              anchorText: linkInfo ? linkInfo.anchorText : 'نامشخص',
              isCorrect: isCorrect,
              url: incoming.url,
              levelDifference: levelDiff
            });

            if (isCorrect) {
              cluster.incomingLinks++;
              if (levelDiff > 1) {
                cluster.errors.push(
                  `صفه "${incoming.title}" با ${incoming.keywordCount} کلمه کلیدی به "${page.title}" با ${page.keywordCount} کلمه کلیدی لینک داده (پرش ${levelDiff} سطحی)`
                );
                if (cluster.health !== 'unhealthy') cluster.health = 'warning';
              }
            } else {
              cluster.errors.push(
                `صفحه "${incoming.title}" با ${incoming.keywordCount} کلمه کلیدی به "${page.title}" با ${page.keywordCount} کلمه کلیدی لینک داده (اشتباه)`
              );
              cluster.health = 'unhealthy';
            }
          });

          const missingLinks = this.findMissingLinks(page, pageMap);
          missingLinks.forEach(missing => {
            cluster.missingLinks.push(missing);
            if (cluster.health !== 'unhealthy') cluster.health = 'warning';
          });

          if (cluster.incomingLinks === 0 && cluster.outgoingLinks === 0) {
            cluster.health = 'unhealthy';
            cluster.errors.push(
              `صفحه "${cluster.title}" کاملاً یتیم است! هیچ لینک ورودی یا خروجی ندارد.`,
              `پیشنهاد: حداقل یک لینک از صفحه اصلی یا مقالات دیگر به این صفحه اضافه کنید.`
            );
          } else if (cluster.incomingLinks === 0 && cluster.outgoingLinks > 0) {
            cluster.health = 'unhealthy';
            cluster.errors.push(
              `صفحه "${cluster.title}" یتیم است! (${cluster.outgoingLinks} لینک خروجی، ۰ ورودی).`,
              `پیشنهاد: از صفحات دیگر به این صفحه لینک دهید.`
            );
          } else if (cluster.incomingLinks > 0 && cluster.outgoingLinks === 0) {
            if (cluster.health !== 'unhealthy') cluster.health = 'warning';
            cluster.errors.push(
              `صفحه "${cluster.title}" فقط لینک ورودی دارد (${cluster.incomingLinks}) و هیچ لینک خروجی ندارد.`,
              `پیشنهاد: از این صفحه به صفحات مرتبط لینک دهید.`
            );
          } else if (cluster.incomingLinks > 0 && cluster.outgoingLinks > 0 && cluster.errors.length === 0) {
            cluster.health = 'healthy';
          }

          clusters.push(cluster);
        });
      });

      return clusters;
    },
    filteredClusters() {
      let filtered = this.clusters;

      if (this.filterStatus !== 'all') {
        filtered = filtered.filter(c => c.health === this.filterStatus);
      }
      if (this.searchQuery) {
        const query = this.searchQuery.toLowerCase();
        filtered = filtered.filter(c =>
          c.title.toLowerCase().includes(query) ||
          c.linkedPages.some(p => p.title.toLowerCase().includes(query))
        );
      }

      switch (this.sortBy) {
        case 'keywordCount':
          filtered.sort((a, b) => a.keywordCount - b.keywordCount);
          break;
        case 'keywordCountDesc':
          filtered.sort((a, b) => b.keywordCount - a.keywordCount);
          break;
        case 'health': {
          const order = { unhealthy: 1, warning: 2, healthy: 3 };
          filtered.sort((a, b) => order[a.health] - order[b.health]);
          break;
        }
        case 'links':
          filtered.sort((a, b) => (b.incomingLinks + b.outgoingLinks) - (a.incomingLinks + a.outgoingLinks));
          break;
      }

      return filtered;
    },
    correctLinks() {
      return this.clusters.reduce((sum, c) => sum + c.linkedPages.filter(p => p.isCorrect).length, 0);
    },
    incorrectLinks() {
      return this.clusters.reduce((sum, c) => sum + c.linkedPages.filter(p => !p.isCorrect).length, 0);
    },
    missingLinks() {
      return this.clusters.reduce((sum, c) => sum + c.missingLinks.length, 0);
    },
    totalInternalLinks() {
      return this.clusters.reduce((sum, c) => sum + c.incomingLinks + c.outgoingLinks, 0);
    },
    totalClusters() {
      return this.clusters.length;
    },
    totalLinkedPages() {
      return this.clusters.reduce((sum, c) => sum + c.linkedPages.length, 0);
    },
    totalAll() {
      return this.correctLinks + this.incorrectLinks + this.missingLinks || 1;
    },
    correctPercent() {
      return Math.round((this.correctLinks / this.totalAll) * 100);
    },
    incorrectPercent() {
      return Math.round((this.incorrectLinks / this.totalAll) * 100);
    },
    missingPercent() {
      return Math.round((this.missingLinks / this.totalAll) * 100);
    },
    overallBadgeClass() {
      const u = this.clusters.filter(c => c.health === 'unhealthy').length;
      const w = this.clusters.filter(c => c.health === 'warning').length;
      if (u > 0) return 'tcla-badge--red';
      if (w > 0) return 'tcla-badge--amber';
      return 'tcla-badge--green';
    },
    overallStatusText() {
      const u = this.clusters.filter(c => c.health === 'unhealthy').length;
      const w = this.clusters.filter(c => c.health === 'warning').length;
      const h = this.clusters.filter(c => c.health === 'healthy').length;
      if (u > 0) return `${u} مشکل`;
      if (w > 0) return `${w} هشدار`;
      return `${h} سالم`;
    }
  },
  watch: {
    filteredClusters(val) {
      val.forEach((_, i) => {
        if (this.openClusters[i] === undefined) {
          this.$set(this.openClusters, i, i < 3);
        }
      });
    }
  },
  created() {
    this.filteredClusters.forEach((_, i) => {
      this.$set(this.openClusters, i, i < 3);
    });
  },
  methods: {
    copy_data(){
      this.copyText(JSON.stringify(this.filteredClusters), 'متن کپی شد');

    },
    open_edit_blog(source){
      window.open("/dashboard/news/edit-news/"+source)
    },
    toggleCluster(index) {
      this.$set(this.openClusters, index, !this.openClusters[index]);
    },
    showToast(msg) {
      this.toastMessage = msg;
      this.toastVisible = true;
      setTimeout(() => { this.toastVisible = false; }, 2500);
    },
    buildPageMap() {
      const map = {};
      this.items.forEach(item => {
        const keywordCount = this.countKeywords(item.keyword || '');
        map[item.url] = {
          title: item.title,
          url: item.url,
          source: item.source,
          keyword: item.keyword,
          keywordCount: keywordCount,
          htmlContent: item.htmlContent,
          outgoingLinks: this.extractOutgoingLinks(item.htmlContent)
        };
      });
      return map;
    },
    extractOutgoingLinks(htmlContent) {
      if (!htmlContent) return [];
      const parser = new DOMParser();
      const doc = parser.parseFromString(htmlContent, 'text/html');
      const links = [];
      doc.querySelectorAll('a').forEach(link => {
        const href = link.getAttribute('href');
        const anchorText = link.textContent.trim();
        if (href && href.includes('/')) links.push({ url: href, anchorText });
      });
      return links;
    },
    groupPagesByKeywordCount(pageMap) {
      const grouped = {};
      Object.values(pageMap).forEach(page => {
        const count = page.keywordCount;
        if (!grouped[count]) grouped[count] = [];
        grouped[count].push(page);
      });
      return grouped;
    },
    getIncomingPages(page, pageMap) {
      const incoming = [];
      Object.values(pageMap).forEach(p => {
        if (p.url === page.url) return;
        if (p.outgoingLinks.some(link => link.url === page.url || decodeURI(link.url) ===  page.url)) {
          incoming.push(p);
        }
      });
      return incoming;
    },
    findLinkInfo(fromPage, toPage) {
      return fromPage.outgoingLinks.find(l => l.url === toPage.url || l.url.includes(toPage.url)) || null;
    },
    findMissingLinks(page, pageMap) {
      const missing = [];
      const targetKeywordCount = page.keywordCount + 1;
      Object.values(pageMap).forEach(p => {
        if (p.url === page.url) return;
        if (p.keywordCount === targetKeywordCount) {
          const hasLink = p.outgoingLinks.some(link => link.url === page.url || link.url.includes(page.url));
          if (!hasLink) {
            const relationResult = this.checkThematicRelation(p.keyword, page.keyword);
            if (relationResult.isRelated) {
              missing.push({
                title: p.title, keyword: p.keyword, keywordCount: p.keywordCount,
                source:p.source,
                suggestedAnchor: page.title, targetTitle: page.title,
                targetKeyword: page.keyword, targetKeywordCount: page.keywordCount,
                targetUrl: page.url, commonWords: relationResult.commonWords,
                relationType: relationResult.type,
                reason: `صفحه "${p.title}" (${p.keyword}) با ${p.keywordCount} کلمه باید به "${page.title}" با ${page.keywordCount} کلمه لینک دهد`
              });
            }
          }
        }
      });
      return missing;
    },
    checkThematicRelation(keyword1, keyword2) {
      if (!keyword1 || !keyword2) return { isRelated: false, commonWords: [], type: 'بدون ارتباط' };
      const clean1 = this.removeStopWords(keyword1).join(' ');
      const clean2 = this.removeStopWords(keyword2).join(' ');
      if (!clean1 || !clean2) return { isRelated: false, commonWords: [], type: 'بدون کلمه کلیدی' };
      const words1 = clean1.split(' ');
      const words2 = clean2.split(' ');
      const allWordsExist = words2.every(word => words1.includes(word));
      if (allWordsExist) {
        const commonWords = words2.filter(word => words1.includes(word));
        return { isRelated: true, commonWords, type: `مرتبط (${commonWords.length} کلمه مشترک)` };
      }
      return { isRelated: false, commonWords: [], type: 'بدون ارتباط کامل' };
    },
    removeStopWords(text) {
      if (!text) return [];
      return text.split(/\s+/).filter(w => w.length > 0).filter(word => {
        const clean = word.replace(/[^آ-یa-zA-Z]/g, '').toLowerCase();
        return !this.stopWords.includes(clean) && clean.length > 0;
      });
    },
    countKeywords(text) {
      if (!text) return 0;
      return text.split(/\s+/).filter(w => w.length > 0).filter(word => {
        const clean = word.replace(/[^آ-یa-zA-Z]/g, '').toLowerCase();
        return !this.stopWords.includes(clean) && clean.length > 0;
      }).length;
    },
    showSuggestion(page, cluster) {
      let problem = '', solution = '';
      if (!page.isCorrect) {
        problem = `صفحه "${page.title}" (${page.keywordCount} کلمه) به "${cluster.title}" (${cluster.keywordCount} کلمه) لینک داده.`;
        solution = `صفحات با کلمات بیشتر باید به صفحات با کلمات کمتر لینک دهند.`;
      } else if (page.levelDifference > 1) {
        problem = `پرش ${page.levelDifference} سطحی از "${page.title}" به "${cluster.title}".`;
        solution = `بهتر است مسیر لایه‌ای استفاده شود: ${page.keywordCount} → ${page.keywordCount - 1} → ... → ${cluster.keywordCount}`;
      }
      this.selectedSuggestion = { problem, solution, suggestedAnchor: cluster.title, example: `<a href="${cluster.url}">${cluster.title}</a>` };
      this.showModal = true;
    },
    showMissingSuggestion(missing) {
      this.selectedMissing = missing;
      this.showMissingModal = true;
    },
    copySuggestion() {
      if (!this.selectedMissing) return;
      const text = `برای اطلاعات بیشتر در مورد ${this.selectedMissing.suggestedAnchor}، مقاله جامع ما را مطالعه کنید.`;
      this.copyText(text, 'متن کپی شد');
    },
    copyLinkSuggestion(missing) {
      const text = `برای اطلاعات بیشتر در مورد ${missing.suggestedAnchor}، مقاله جامع ما را مطالعه کنید.`;
      this.copyText(text, 'متن لینک کپی شد');
    },
    copyText(text, msg) {
      if (navigator.clipboard) {
        navigator.clipboard.writeText(text).then(() => this.showToast(msg));
      } else {
        const ta = document.createElement('textarea');
        ta.value = text;
        document.body.appendChild(ta);
        ta.select();
        document.execCommand('copy');
        document.body.removeChild(ta);
        this.showToast(msg);
      }
    },
    getHealthLabel(health) {
      return { healthy: 'سالم', warning: 'هشدار', unhealthy: 'مشکل‌دار' }[health] || health;
    }
  }
};
</script>

<style scoped>
@import url('https://fonts.googleapis.com/css2?family=Vazirmatn:wght@300;400;500;600;700&display=swap');

/* =============================================
   DESIGN TOKENS
   ============================================= */
.tcla {
  --c-bg: #f5f3ef;
  --c-surface: #ffffff;
  --c-surface-alt: #faf9f7;
  --c-border: #e8e5df;
  --c-border-light: #f0ede8;
  --c-text: #2c2a26;
  --c-text-secondary: #6b6760;
  --c-text-muted: #9d9890;
  --c-accent: #4a6cf7;
  --c-accent-light: #eef1fe;
  --c-success: #22c55e;
  --c-success-light: #ecfdf5;
  --c-success-border: #bbf7d0;
  --c-danger: #ef4444;
  --c-danger-light: #fef2f2;
  --c-danger-border: #fecaca;
  --c-warning: #f59e0b;
  --c-warning-light: #fffbeb;
  --c-warning-border: #fde68a;
  --c-blue: #3b82f6;
  --c-blue-light: #eff6ff;
  --c-blue-border: #bfdbfe;
  --radius-sm: 6px;
  --radius-md: 10px;
  --radius-lg: 14px;
  --radius-xl: 18px;
  --shadow-sm: 0 1px 2px rgba(44,42,38,0.04);
  --shadow-md: 0 2px 8px rgba(44,42,38,0.06), 0 1px 2px rgba(44,42,38,0.04);
  --shadow-lg: 0 4px 20px rgba(44,42,38,0.08), 0 2px 6px rgba(44,42,38,0.04);
  --shadow-xl: 0 8px 40px rgba(44,42,38,0.12);

  font-family: 'Vazirmatn', sans-serif;
  direction: rtl;
  text-align: right;
  background: var(--c-bg);
  color: var(--c-text);
  padding: 24px;
  min-height: 100vh;
}

/* =============================================
   HEADER
   ============================================= */
.tcla-header {
  background: var(--c-surface);
  border-radius: var(--radius-xl);
  padding: 28px 32px;
  margin-bottom: 20px;
  box-shadow: var(--shadow-md);
  border: 1px solid var(--c-border-light);
}

.tcla-header__top {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 16px;
  flex-wrap: wrap;
  margin-bottom: 28px;
}

.tcla-header__title {
  font-size: 1.35rem;
  font-weight: 700;
  color: var(--c-text);
  margin: 0 0 4px 0;
  display: flex;
  align-items: center;
  gap: 10px;
}

.tcla-header__icon {
  font-size: 1.3rem;
}

.tcla-header__subtitle {
  font-size: 0.82rem;
  color: var(--c-text-muted);
  margin: 0;
  padding-right: 36px;
}

.tcla-header__badges {
  display: flex;
  gap: 8px;
  align-items: center;
  flex-wrap: wrap;
}

/* Badges */
.tcla-badge {
  display: inline-flex;
  align-items: center;
  padding: 5px 14px;
  border-radius: 100px;
  font-size: 0.78rem;
  font-weight: 600;
  white-space: nowrap;
}

.tcla-badge--blue { background: var(--c-accent-light); color: var(--c-accent); }
.tcla-badge--green { background: var(--c-success-light); color: #16a34a; }
.tcla-badge--red { background: var(--c-danger-light); color: var(--c-danger); }
.tcla-badge--amber { background: var(--c-warning-light); color: #d97706; }

/* =============================================
   STATS
   ============================================= */
.tcla-stats {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 14px;
}

.tcla-stat {
  background: var(--c-surface-alt);
  border-radius: var(--radius-md);
  padding: 18px 20px;
  border: 1px solid var(--c-border-light);
  transition: transform 0.2s ease, box-shadow 0.2s ease;
}

.tcla-stat:hover {
  transform: translateY(-1px);
  box-shadow: var(--shadow-sm);
}

.tcla-stat__value {
  font-size: 1.9rem;
  font-weight: 700;
  line-height: 1;
  margin-bottom: 4px;
}

.tcla-stat--success .tcla-stat__value { color: var(--c-success); }
.tcla-stat--danger .tcla-stat__value { color: var(--c-danger); }
.tcla-stat--warning .tcla-stat__value { color: var(--c-warning); }
.tcla-stat--neutral .tcla-stat__value { color: var(--c-text-secondary); }

.tcla-stat__label {
  font-size: 0.78rem;
  color: var(--c-text-muted);
  margin-bottom: 12px;
  font-weight: 500;
}

.tcla-stat__bar {
  height: 4px;
  background: var(--c-border-light);
  border-radius: 100px;
  overflow: hidden;
}

.tcla-stat__bar-fill {
  height: 100%;
  border-radius: 100px;
  transition: width 0.8s cubic-bezier(0.22, 1, 0.36, 1);
}

.tcla-stat--success .tcla-stat__bar-fill { background: var(--c-success); }
.tcla-stat--danger .tcla-stat__bar-fill { background: var(--c-danger); }
.tcla-stat--warning .tcla-stat__bar-fill { background: var(--c-warning); }
.tcla-stat--neutral .tcla-stat__bar-fill { background: var(--c-text-muted); }
.tcla-stat__bar-fill--full { width: 100% !important; }

/* =============================================
   FILTERS
   ============================================= */
.tcla-filters {
  display: flex;
  gap: 12px;
  margin-bottom: 20px;
  flex-wrap: wrap;
}

.tcla-filter {
  flex: 1;
  min-width: 160px;
}

.tcla-filter--search {
  flex: 1.5;
}

.tcla-filter__label {
  display: block;
  font-size: 0.72rem;
  font-weight: 600;
  color: var(--c-text-muted);
  margin-bottom: 6px;
  text-transform: uppercase;
  letter-spacing: 0.04em;
}

.tcla-filter__select {
  width: 100%;
  padding: 10px 14px;
  border: 1px solid var(--c-border);
  border-radius: var(--radius-md);
  background: var(--c-surface);
  font-family: inherit;
  font-size: 0.85rem;
  color: var(--c-text);
  appearance: none;
  cursor: pointer;
  transition: border-color 0.2s, box-shadow 0.2s;
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 24 24' fill='none' stroke='%239d9890' stroke-width='2'%3E%3Cpolyline points='6 9 12 15 18 9'/%3E%3C/svg%3E");
  background-repeat: no-repeat;
  background-position: 14px center;
  padding-left: 32px;
}

.tcla-filter__select:focus {
  outline: none;
  border-color: var(--c-accent);
  box-shadow: 0 0 0 3px rgba(74, 108, 247, 0.1);
}

.tcla-search {
  position: relative;
}

.tcla-search__icon {
  position: absolute;
  right: 14px;
  top: 50%;
  transform: translateY(-50%);
  color: var(--c-text-muted);
  pointer-events: none;
}

.tcla-search__input {
  width: 100%;
  padding: 10px 14px;
  padding-right: 40px;
  border: 1px solid var(--c-border);
  border-radius: var(--radius-md);
  background: var(--c-surface);
  font-family: inherit;
  font-size: 0.85rem;
  color: var(--c-text);
  transition: border-color 0.2s, box-shadow 0.2s;
  box-sizing: border-box;
}

.tcla-search__input::placeholder {
  color: var(--c-text-muted);
}

.tcla-search__input:focus {
  outline: none;
  border-color: var(--c-accent);
  box-shadow: 0 0 0 3px rgba(74, 108, 247, 0.1);
}

/* =============================================
   EMPTY STATE
   ============================================= */
.tcla-empty {
  text-align: center;
  padding: 60px 20px;
  background: var(--c-surface);
  border-radius: var(--radius-xl);
  border: 1px dashed var(--c-border);
}

.tcla-empty__icon {
  font-size: 3rem;
  margin-bottom: 12px;
}

.tcla-empty__text {
  font-size: 1rem;
  font-weight: 600;
  color: var(--c-text-secondary);
  margin: 0 0 4px 0;
}

.tcla-empty__hint {
  font-size: 0.82rem;
  color: var(--c-text-muted);
  margin: 0;
}

/* =============================================
   CLUSTERS
   ============================================= */
.tcla-clusters {
  display: flex;
  flex-direction: column;
  gap: 14px;
}

.tcla-cluster {
  background: var(--c-surface);
  border-radius: var(--radius-lg);
  border: 1px solid var(--c-border-light);
  box-shadow: var(--shadow-sm);
  overflow: hidden;
  animation: clusterSlideUp 0.4s ease forwards;
  opacity: 0;
  transition: box-shadow 0.25s ease;
}

.tcla-cluster:hover {
  box-shadow: var(--shadow-md);
}

.tcla-cluster--healthy { border-right: 4px solid var(--c-success); }
.tcla-cluster--warning { border-right: 4px solid var(--c-warning); }
.tcla-cluster--unhealthy { border-right: 4px solid var(--c-danger); }

@keyframes clusterSlideUp {
  from { opacity: 0; transform: translateY(12px); }
  to { opacity: 1; transform: translateY(0); }
}

/* Cluster Header */
.tcla-cluster__header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 18px 24px;
  cursor: pointer;
  transition: background 0.15s;
  user-select: none;
}

.tcla-cluster__header:hover {
  background: var(--c-surface-alt);
}

.tcla-cluster__header-right {
  display: flex;
  align-items: center;
  gap: 14px;
}

.tcla-cluster__folder {
  font-size: 1.5rem;
  flex-shrink: 0;
}

.tcla-cluster__title {
  font-size: 0.95rem;
  font-weight: 600;
  margin: 0 0 4px 0;
  color: var(--c-text);
}

.tcla-cluster__keyword-tag {
  font-weight: 400;
  color: var(--c-text-muted);
  font-size: 0.82rem;
}

.tcla-cluster__meta {
  font-size: 0.75rem;
  color: var(--c-text-muted);
  display: flex;
  align-items: center;
  gap: 4px;
  flex-wrap: wrap;
}

.tcla-cluster__meta-sep {
  color: var(--c-border);
  margin: 0 2px;
}

.tcla-cluster__health-dot {
  display: inline-block;
  width: 7px;
  height: 7px;
  border-radius: 50%;
  margin-left: 4px;
}

.tcla-cluster__health-dot--healthy { background: var(--c-success); }
.tcla-cluster__health-dot--warning { background: var(--c-warning); }
.tcla-cluster__health-dot--unhealthy { background: var(--c-danger); }

.tcla-cluster__chevron {
  transition: transform 0.25s ease;
  color: var(--c-text-muted);
  flex-shrink: 0;
}

.tcla-cluster__chevron--open {
  transform: rotate(180deg);
}

/* Cluster Body */
.tcla-cluster__body {
  padding: 0 24px 24px;
  border-top: 1px solid var(--c-border-light);
}

/* Rule Box */
.tcla-rule {
  display: flex;
  align-items: center;
  gap: 10px;
  background: var(--c-accent-light);
  border-radius: var(--radius-sm);
  padding: 12px 16px;
  margin: 18px 0;
  font-size: 0.8rem;
  color: var(--c-accent);
  border: 1px solid rgba(74, 108, 247, 0.12);
}

.tcla-rule__icon { font-size: 1rem; flex-shrink: 0; }

.tcla-rule__text { flex: 1; line-height: 1.6; }

.tcla-rule__check {
  width: 22px;
  height: 22px;
  border-radius: 50%;
  background: var(--c-success);
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.7rem;
  font-weight: 700;
  flex-shrink: 0;
}

.tcla-rule__warn {
  width: 22px;
  height: 22px;
  border-radius: 50%;
  background: var(--c-warning);
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.7rem;
  font-weight: 700;
  flex-shrink: 0;
}

/* Section */
.tcla-section {
  margin-top: 22px;
}

.tcla-section__title {
  font-size: 0.82rem;
  font-weight: 600;
  color: var(--c-text-secondary);
  margin: 0 0 10px 0;
  display: flex;
  align-items: center;
  gap: 6px;
}

.tcla-section__title--err { color: var(--c-danger); }
.tcla-section__title--warn { color: var(--c-warning); }

/* Linked Pages */
.tcla-linked-list {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.tcla-linked {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 16px;
  border-radius: var(--radius-sm);
  border: 1px solid var(--c-border-light);
  transition: background 0.15s;
  gap: 12px;
  flex-wrap: wrap;
}

.tcla-linked--ok {
  background: var(--c-success-light);
  border-color: var(--c-success-border);
}

.tcla-linked--warn {
  background: var(--c-warning-light);
  border-color: var(--c-warning-border);
}

.tcla-linked--err {
  background: var(--c-danger-light);
  border-color: var(--c-danger-border);
}

.tcla-linked__left {
  display: flex;
  align-items: center;
  gap: 10px;
  min-width: 0;
  flex: 1;
}

.tcla-linked__status-icon {
  width: 26px;
  height: 26px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.72rem;
  font-weight: 700;
  flex-shrink: 0;
  color: white;
}

.tcla-linked--ok .tcla-linked__status-icon { background: var(--c-success); }
.tcla-linked--warn .tcla-linked__status-icon { background: var(--c-warning); }
.tcla-linked--err .tcla-linked__status-icon { background: var(--c-danger); }

.tcla-linked__info {
  display: flex;
  flex-direction: column;
  min-width: 0;
}

.tcla-linked__title {
  font-size: 0.85rem;
  font-weight: 500;
  color: var(--c-text);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.tcla-linked__anchor {
  font-size: 0.72rem;
  color: var(--c-text-muted);
}

.tcla-linked__right {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-shrink: 0;
  flex-wrap: wrap;
}

.tcla-linked__kw-count {
  font-size: 0.72rem;
  color: var(--c-text-muted);
  background: var(--c-surface-alt);
  padding: 2px 8px;
  border-radius: 100px;
  border: 1px solid var(--c-border-light);
}

.tcla-linked__direction {
  font-size: 0.72rem;
  font-weight: 600;
  color: var(--c-text-secondary);
  font-family: monospace;
  direction: ltr;
}

/* Tags */
.tcla-tag {
  display: inline-flex;
  align-items: center;
  padding: 3px 10px;
  border-radius: 100px;
  font-size: 0.7rem;
  font-weight: 600;
  white-space: nowrap;
}

.tcla-tag--ok { background: var(--c-success-light); color: #16a34a; border: 1px solid var(--c-success-border); }
.tcla-tag--warn { background: var(--c-warning-light); color: #d97706; border: 1px solid var(--c-warning-border); }
.tcla-tag--err { background: var(--c-danger-light); color: var(--c-danger); border: 1px solid var(--c-danger-border); }
.tcla-tag--blue { background: var(--c-blue-light); color: var(--c-blue); border: 1px solid var(--c-blue-border); }
.tcla-tag--green { background: var(--c-success-light); color: #16a34a; border: 1px solid var(--c-success-border); }

/* Buttons */
.tcla-btn-link {
  background: none;
  border: none;
  color: var(--c-accent);
  font-family: inherit;
  font-size: 0.75rem;
  font-weight: 600;
  cursor: pointer;
  padding: 4px 8px;
  border-radius: var(--radius-sm);
  transition: background 0.15s;
}

.tcla-btn-link:hover {
  background: var(--c-accent-light);
}

.tcla-btn {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 8px 18px;
  border-radius: var(--radius-md);
  font-family: inherit;
  font-size: 0.82rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
  border: none;
}

.tcla-btn--primary {
  background: var(--c-accent);
  color: white;
}

.tcla-btn--primary:hover {
  background: #3b5de7;
  box-shadow: var(--shadow-md);
}

.tcla-btn--outline {
  background: var(--c-surface);
  color: var(--c-text-secondary);
  border: 1px solid var(--c-border);
}

.tcla-btn--outline:hover {
  border-color: var(--c-accent);
  color: var(--c-accent);
  background: var(--c-accent-light);
}

.tcla-btn--sm {
  padding: 6px 14px;
  font-size: 0.75rem;
}

/* Errors */
.tcla-errors {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.tcla-error-item {
  background: var(--c-danger-light);
  border: 1px solid var(--c-danger-border);
  border-radius: var(--radius-sm);
  padding: 10px 14px;
  font-size: 0.8rem;
  color: #b91c1c;
  line-height: 1.6;
}

/* Missing Links */
.tcla-missing-list {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.tcla-missing {
  background: var(--c-warning-light);
  border: 1px solid var(--c-warning-border);
  border-radius: var(--radius-md);
  padding: 16px 18px;
}

.tcla-missing__top {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 12px;
  flex-wrap: wrap;
}

.tcla-missing__info {
  flex: 1;
  min-width: 0;
}

.tcla-missing__reason {
  font-size: 0.82rem;
  color: var(--c-text);
  display: block;
  margin-bottom: 6px;
  line-height: 1.6;
}

.tcla-missing__tags {
  display: flex;
  gap: 6px;
  flex-wrap: wrap;
}

.tcla-common-words {
  margin-top: 10px;
  display: flex;
  align-items: center;
  gap: 6px;
  flex-wrap: wrap;
}

.tcla-common-words__label {
  font-size: 0.72rem;
  color: var(--c-text-muted);
}

.tcla-word-chip {
  display: inline-flex;
  align-items: center;
  padding: 2px 10px;
  background: var(--c-surface);
  border: 1px solid var(--c-border);
  border-radius: 100px;
  font-size: 0.72rem;
  color: var(--c-text-secondary);
  font-weight: 500;
}

/* Suggestion Box */
.tcla-suggestion-box {
  margin-top: 14px;
  background: var(--c-surface);
  border: 1px solid var(--c-border);
  border-radius: var(--radius-md);
  overflow: hidden;
}

.tcla-suggestion-box__header {
  background: var(--c-accent-light);
  padding: 10px 16px;
  font-size: 0.8rem;
  font-weight: 600;
  color: var(--c-accent);
  border-bottom: 1px solid rgba(74, 108, 247, 0.1);
}

.tcla-suggestion-box__body {
  padding: 14px 16px;
  font-size: 0.82rem;
  line-height: 1.7;
  color: var(--c-text-secondary);
}

.tcla-suggestion-box__body p { margin: 0 0 8px; }
.tcla-suggestion-box__body code {
  background: var(--c-success-light);
  color: #16a34a;
  padding: 1px 6px;
  border-radius: 4px;
  font-size: 0.8rem;
}

.tcla-suggestion-box__example {
  color: var(--c-text-muted) !important;
  font-size: 0.78rem !important;
  font-style: italic;
}

.tcla-suggestion-box__example a {
  color: var(--c-accent);
  text-decoration: underline;
}

/* =============================================
   MODAL
   ============================================= */
.tcla-modal-overlay {
  position: fixed;
  inset: 0;
  background: rgba(44, 42, 38, 0.35);
  backdrop-filter: blur(4px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
  padding: 20px;
  animation: fadeIn 0.2s ease;
}

@keyframes fadeIn {
  from { opacity: 0; }
  to { opacity: 1; }
}

.tcla-modal {
  background: var(--c-surface);
  border-radius: var(--radius-xl);
  width: 100%;
  max-width: 600px;
  max-height: 85vh;
  display: flex;
  flex-direction: column;
  box-shadow: var(--shadow-xl);
  animation: modalSlideUp 0.25s ease;
  border: 1px solid var(--c-border-light);
}

@keyframes modalSlideUp {
  from { opacity: 0; transform: translateY(20px); }
  to { opacity: 1; transform: translateY(0); }
}

.tcla-modal__header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 20px 24px;
  border-bottom: 1px solid var(--c-border-light);
}

.tcla-modal__header h3 {
  font-size: 1rem;
  font-weight: 700;
  margin: 0;
  color: var(--c-text);
}

.tcla-modal__close {
  width: 32px;
  height: 32px;
  border-radius: 50%;
  border: none;
  background: var(--c-surface-alt);
  color: var(--c-text-muted);
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.9rem;
  transition: all 0.15s;
}

.tcla-modal__close:hover {
  background: var(--c-danger-light);
  color: var(--c-danger);
}

.tcla-modal__body {
  padding: 24px;
  overflow-y: auto;
  flex: 1;
}

.tcla-modal__section {
  margin-bottom: 20px;
}

.tcla-modal__section:last-child {
  margin-bottom: 0;
}

.tcla-modal__section h4 {
  font-size: 0.75rem;
  font-weight: 600;
  color: var(--c-text-muted);
  text-transform: uppercase;
  letter-spacing: 0.04em;
  margin: 0 0 8px 0;
}

.tcla-modal__section p {
  margin: 0;
  font-size: 0.85rem;
  line-height: 1.7;
}

.tcla-modal__problem { color: var(--c-danger); }
.tcla-modal__solution { color: #16a34a; }
.tcla-modal__warn { color: #d97706; }
.tcla-modal__muted { color: var(--c-text-muted); font-size: 0.8rem !important; }

.tcla-modal__code-block {
  background: var(--c-surface-alt);
  border: 1px solid var(--c-border);
  border-radius: var(--radius-sm);
  padding: 12px 16px;
  font-family: 'DM Mono', 'Courier New', monospace;
  font-size: 0.82rem;
  color: var(--c-text);
  direction: ltr;
  text-align: left;
  word-break: break-all;
}

.tcla-modal__code-block--dark {
  background: #1e1e2e;
  color: #cdd6f4;
  border-color: #313244;
}

.tcla-modal__example-block {
  background: var(--c-accent-light);
  border: 1px solid rgba(74, 108, 247, 0.15);
  border-radius: var(--radius-sm);
  padding: 12px 16px;
  font-size: 0.85rem;
  color: var(--c-accent);
  direction: ltr;
  text-align: left;
}

.tcla-modal__highlight {
  background: var(--c-success-light);
  border: 1px solid var(--c-success-border);
  border-radius: var(--radius-sm);
  padding: 12px 16px;
  font-size: 0.85rem;
  line-height: 1.7;
  color: var(--c-text);
}

.tcla-modal__highlight code {
  background: rgba(22, 163, 74, 0.1);
  color: #16a34a;
  padding: 1px 6px;
  border-radius: 4px;
}

.tcla-modal__highlight--blue {
  background: var(--c-accent-light);
  border-color: rgba(74, 108, 247, 0.2);
}

.tcla-modal__footer {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  padding: 16px 24px;
  border-top: 1px solid var(--c-border-light);
}

/* =============================================
   TOAST
   ============================================= */
.tcla-toast {
  position: fixed;
  bottom: 24px;
  left: 50%;
  transform: translateX(-50%);
  background: var(--c-text);
  color: white;
  padding: 12px 24px;
  border-radius: 100px;
  font-size: 0.85rem;
  font-weight: 500;
  display: flex;
  align-items: center;
  gap: 8px;
  box-shadow: var(--shadow-lg);
  z-index: 99999;
}

.tcla-toast__icon {
  width: 20px;
  height: 20px;
  border-radius: 50%;
  background: var(--c-success);
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.65rem;
  font-weight: 700;
}

.toast-enter-active { animation: toastIn 0.3s ease; }
.toast-leave-active { animation: toastOut 0.25s ease; }

@keyframes toastIn {
  from { opacity: 0; transform: translateX(-50%) translateY(20px); }
  to { opacity: 1; transform: translateX(-50%) translateY(0); }
}

@keyframes toastOut {
  from { opacity: 1; transform: translateX(-50%) translateY(0); }
  to { opacity: 0; transform: translateX(-50%) translateY(20px); }
}

/* =============================================
   RESPONSIVE
   ============================================= */
@media (max-width: 900px) {
  .tcla { padding: 16px; }
  .tcla-stats { grid-template-columns: repeat(2, 1fr); }
  .tcla-header { padding: 20px; }
  .tcla-header__top { flex-direction: column; }
  .tcla-cluster__header { padding: 14px 16px; }
  .tcla-cluster__body { padding: 0 16px 16px; }
  .tcla-linked { flex-direction: column; align-items: flex-start; }
  .tcla-linked__right { width: 100%; justify-content: flex-end; }
}

@media (max-width: 600px) {
  .tcla-stats { grid-template-columns: 1fr 1fr; gap: 8px; }
  .tcla-filters { flex-direction: column; }
  .tcla-filter { min-width: 100%; }
  .tcla-missing__top { flex-direction: column; }
}
</style>